'use client';

import { trackStorefrontClick } from '@/libs/analytics/storefrontEvents';
import { useStorefrontImpression } from '@/presentation/hooks/useStorefrontImpression';
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Gift, X } from 'lucide-react';

import { campaignAnnouncementDismissalKey, persistCampaignAnnouncementDismissal } from './campaignAnnouncementUtils';
import type { CampaignAnnouncement } from './marketplaceHomeContent';
import { toSafeHref } from '@/shared/utils/url';

interface CampaignAnnouncementStripProps {
  announcement: CampaignAnnouncement | null;
  onCtaClick?: () => void;
  onDismiss?: (announcementId: string) => void;
  wrapMessage?: boolean;
}

export function CampaignAnnouncementStrip({
  announcement,
  onCtaClick,
  onDismiss,
  wrapMessage = false,
}: CampaignAnnouncementStripProps) {
  const [dismissed, setDismissed] = useState<boolean | null>(null);

  const storageKey = useMemo(() => {
    if (!announcement) return '';
    return campaignAnnouncementDismissalKey(announcement.id);
  }, [announcement]);

  useEffect(() => {
    if (!announcement) {
      setDismissed(null);
      return;
    }

    if (!announcement.dismissible || typeof window === 'undefined') {
      setDismissed(false);
      return;
    }

    try {
      setDismissed(window.sessionStorage.getItem(storageKey) === '1');
    } catch {
      setDismissed(false);
    }
  }, [announcement, storageKey]);

  // Wallet announcement IDs contain account/credit identifiers. Never emit them.
  const analyticsId = announcement?.id.startsWith('shopping-wallet:') ? 'wallet_gift'
    : announcement?.priority === 'critical' ? 'critical_announcement' : 'announcement';
  const impressionRef = useStorefrontImpression('announcement', analyticsId,
    Boolean(announcement) && dismissed === false);

  if (!announcement) {
    return null;
  }

  if (announcement.dismissible && dismissed === null) {
    return null;
  }

  if (dismissed) {
    return null;
  }

  const ctaHref = toSafeHref(announcement.ctaUrl);

  return (
    <section ref={impressionRef}
      className="-mx-6 mt-3 bg-[#141414] sm:mx-0 sm:mt-4"
      aria-label="Campaign announcement"
    >
      <div className="flex min-h-10 items-center justify-between gap-3 px-4 py-0 sm:min-h-11 sm:px-10">
        {/* Left: icon + message */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
          <Gift className="size-3.5 shrink-0 text-[#85E9AD] sm:size-[15px]" />
          <p className={`${wrapMessage ? 'line-clamp-2' : 'line-clamp-1'} min-w-0 text-[11px] font-semibold leading-tight text-white sm:text-[13px] sm:font-medium`}>
            {announcement.message}
            {announcement.detail ? (
              <span className="hidden sm:inline"> {announcement.detail}</span>
            ) : null}
          </p>
        </div>

        {/* Right: CTA + dismiss */}
        <div className="flex shrink-0 items-center gap-3 sm:gap-4">
          {announcement.ctaLabel && ctaHref ? (
            <Link
              href={ctaHref}
              onClick={() => trackStorefrontClick('announcement', analyticsId)}
              data-testid="campaign-announcement-cta-link"
              className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-bold text-[#85E9AD] no-underline hover:no-underline sm:text-[13px]"
            >
              <span className="sm:hidden">{announcement.mobileCtaLabel || announcement.ctaLabel}</span>
              <span className="hidden sm:inline">{announcement.ctaLabel}</span>
              <ArrowRight className="size-3.5 sm:size-4" />
            </Link>
          ) : announcement.ctaLabel && onCtaClick ? (
            <button
              type="button"
              data-testid="campaign-announcement-cta-button"
              onClick={() => { trackStorefrontClick('announcement', analyticsId); onCtaClick(); }}
              className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-bold text-[#85E9AD] no-underline hover:no-underline sm:text-[13px]"
            >
              <span className="sm:hidden">{announcement.mobileCtaLabel || announcement.ctaLabel}</span>
              <span className="hidden sm:inline">{announcement.ctaLabel}</span>
              <ArrowRight className="size-3.5 sm:size-4" />
            </button>
          ) : null}
          {announcement.dismissible ? (
            <button
              type="button"
              aria-label="Dismiss announcement"
              onClick={() => {
                setDismissed(true);
                onDismiss?.(announcement.id);
                persistCampaignAnnouncementDismissal(announcement.id);
              }}
              className="shrink-0 p-0.5"
            >
              <X className="size-3.5 text-white/40 sm:size-[15px] sm:text-white/[0.33]" />
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default CampaignAnnouncementStrip;
